import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdFileDownload, MdPrint } from 'react-icons/md';
import '../master/MasterData.css';

const FinancialStatements: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'balance' | 'income' | 'cashflow'>('balance');
  const [periodStart, setPeriodStart] = useState('2025-01-01');
  const [periodEnd, setPeriodEnd] = useState('2025-02-28');

  // Balance Sheet Data
  const balanceSheetData = {
    assets: {
      current: [
        { account: 'Cash and Cash Equivalents', amount: 5100000 },
        { account: 'Accounts Receivable', amount: 600000 },
        { account: 'Inventory', amount: 3000000 },
        { account: 'Prepaid Expenses', amount: 150000 }
      ],
      nonCurrent: [
        { account: 'Property, Plant & Equipment', amount: 8000000 },
        { account: 'Less: Accumulated Depreciation', amount: -1200000 },
        { account: 'Intangible Assets', amount: 500000 }
      ]
    },
    liabilities: {
      current: [
        { account: 'Accounts Payable', amount: 1300000 },
        { account: 'Accrued Expenses', amount: 250000 },
        { account: 'Short-term Loans', amount: 1000000 }
      ],
      nonCurrent: [
        { account: 'Long-term Loans', amount: 3000000 },
        { account: 'Deferred Tax Liability', amount: 200000 }
      ]
    },
    equity: [
      { account: 'Share Capital', amount: 5000000 },
      { account: 'Retained Earnings', amount: 5400000 }
    ]
  };

  // Income Statement Data
  const incomeStatementData = {
    revenue: [
      { account: 'Sales Revenue', amount: 12500000 },
      { account: 'Less: Sales Returns', amount: -250000 }
    ],
    cogs: [
      { account: 'Cost of Goods Sold', amount: 7000000 }
    ],
    operatingExpenses: [
      { account: 'Salaries and Wages', amount: 2000000 },
      { account: 'Rent Expense', amount: 300000 },
      { account: 'Utilities Expense', amount: 150000 },
      { account: 'Depreciation Expense', amount: 90000 },
      { account: 'Other Operating Expenses', amount: 460000 }
    ],
    otherIncome: [
      { account: 'Interest Income', amount: 50000 },
      { account: 'Other Income', amount: 100000 }
    ],
    otherExpenses: [
      { account: 'Interest Expense', amount: 80000 },
      { account: 'Other Expenses', amount: 20000 }
    ]
  };

  // Cash Flow Statement Data
  const cashFlowData = {
    operating: [
      { account: 'Net Income', amount: 2300000 },
      { account: 'Add: Depreciation', amount: 90000 },
      { account: 'Increase in Accounts Receivable', amount: -100000 },
      { account: 'Increase in Inventory', amount: -500000 },
      { account: 'Increase in Accounts Payable', amount: 200000 }
    ],
    investing: [
      { account: 'Purchase of Equipment', amount: -800000 },
      { account: 'Sale of Assets', amount: 150000 }
    ],
    financing: [
      { account: 'Proceeds from Loans', amount: 1000000 },
      { account: 'Repayment of Loans', amount: -500000 },
      { account: 'Dividends Paid', amount: -300000 }
    ]
  };

  const totalCurrentAssets = balanceSheetData.assets.current.reduce((sum, item) => sum + item.amount, 0);
  const totalNonCurrentAssets = balanceSheetData.assets.nonCurrent.reduce((sum, item) => sum + item.amount, 0);
  const totalAssets = totalCurrentAssets + totalNonCurrentAssets;

  const totalCurrentLiabilities = balanceSheetData.liabilities.current.reduce((sum, item) => sum + item.amount, 0);
  const totalNonCurrentLiabilities = balanceSheetData.liabilities.nonCurrent.reduce((sum, item) => sum + item.amount, 0);
  const totalLiabilities = totalCurrentLiabilities + totalNonCurrentLiabilities;

  const totalEquity = balanceSheetData.equity.reduce((sum, item) => sum + item.amount, 0);
  const totalLiabilitiesAndEquity = totalLiabilities + totalEquity;

  const netSales = incomeStatementData.revenue.reduce((sum, item) => sum + item.amount, 0);
  const totalCOGS = incomeStatementData.cogs.reduce((sum, item) => sum + item.amount, 0);
  const grossProfit = netSales - totalCOGS;
  const totalOperatingExpenses = incomeStatementData.operatingExpenses.reduce((sum, item) => sum + item.amount, 0);
  const operatingIncome = grossProfit - totalOperatingExpenses;
  const totalOtherIncome = incomeStatementData.otherIncome.reduce((sum, item) => sum + item.amount, 0);
  const totalOtherExpenses = incomeStatementData.otherExpenses.reduce((sum, item) => sum + item.amount, 0);
  const netIncome = operatingIncome + totalOtherIncome - totalOtherExpenses;

  const netCashFromOperating = cashFlowData.operating.reduce((sum, item) => sum + item.amount, 0);
  const netCashFromInvesting = cashFlowData.investing.reduce((sum, item) => sum + item.amount, 0);
  const netCashFromFinancing = cashFlowData.financing.reduce((sum, item) => sum + item.amount, 0);
  const netCashIncrease = netCashFromOperating + netCashFromInvesting + netCashFromFinancing;

  const handleExport = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Financial Statements</h2>
            <p className="subtitle">View comprehensive financial reports</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handleExport}>
              <MdFileDownload /> Export to Excel
            </button>
            <button className="btn btn-secondary" onClick={handlePrint}>
              <MdPrint /> Print
            </button>
          </div>
        </div>

        <div className="page-content">
          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: '200px' }}>
              <label>Period Start</label>
              <input
                type="date"
                  lang="en-US"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ minWidth: '200px' }}>
              <label>Period End</label>
              <input
                type="date"
                  lang="en-US"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-tabs" style={{ marginBottom: '20px' }}>
            <button
              className={`filter-tab ${activeTab === 'balance' ? 'active' : ''}`}
              onClick={() => setActiveTab('balance')}
            >
              Balance Sheet
            </button>
            <button
              className={`filter-tab ${activeTab === 'income' ? 'active' : ''}`}
              onClick={() => setActiveTab('income')}
            >
              Income Statement
            </button>
            <button
              className={`filter-tab ${activeTab === 'cashflow' ? 'active' : ''}`}
              onClick={() => setActiveTab('cashflow')}
            >
              Cash Flow Statement
            </button>
          </div>

          {activeTab === 'balance' && (
            <div>
              <h3 style={{ textAlign: 'center', marginBottom: '10px', color: '#1a237e' }}>
                DEWFOAM ACCOUNTING SYSTEM
              </h3>
              <h4 style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
                Balance Sheet (Statement of Financial Position)
              </h4>
              <p style={{ textAlign: 'center', marginBottom: '30px', color: '#666' }}>
                As of {new Date(periodEnd).toLocaleDateString('en-US')}
              </p>

              <table className="data-table">
                <tbody>
                  <tr style={{ backgroundColor: '#1a237e', color: 'white' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', fontSize: '16px' }}>ASSETS</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <td style={{ fontWeight: 'bold', paddingLeft: '20px' }}>Current Assets</td>
                    <td></td>
                  </tr>
                  {balanceSheetData.assets.current.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '30px' }}>Total Current Assets</td>
                    <td className="amount">₱{totalCurrentAssets.toLocaleString()}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <td style={{ fontWeight: 'bold', paddingLeft: '20px' }}>Non-Current Assets</td>
                    <td></td>
                  </tr>
                  {balanceSheetData.assets.nonCurrent.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '30px' }}>Total Non-Current Assets</td>
                    <td className="amount">₱{totalNonCurrentAssets.toLocaleString()}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#1a237e', color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                    <td>TOTAL ASSETS</td>
                    <td className="amount">₱{totalAssets.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '20px' }}></td></tr>

                  <tr style={{ backgroundColor: '#1a237e', color: 'white' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', fontSize: '16px' }}>LIABILITIES</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <td style={{ fontWeight: 'bold', paddingLeft: '20px' }}>Current Liabilities</td>
                    <td></td>
                  </tr>
                  {balanceSheetData.liabilities.current.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '30px' }}>Total Current Liabilities</td>
                    <td className="amount">₱{totalCurrentLiabilities.toLocaleString()}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <td style={{ fontWeight: 'bold', paddingLeft: '20px' }}>Non-Current Liabilities</td>
                    <td></td>
                  </tr>
                  {balanceSheetData.liabilities.nonCurrent.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '30px' }}>Total Non-Current Liabilities</td>
                    <td className="amount">₱{totalNonCurrentLiabilities.toLocaleString()}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '20px' }}>TOTAL LIABILITIES</td>
                    <td className="amount">₱{totalLiabilities.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '10px' }}></td></tr>

                  <tr style={{ backgroundColor: '#1a237e', color: 'white' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', fontSize: '16px' }}>EQUITY</td>
                  </tr>
                  {balanceSheetData.equity.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '20px' }}>TOTAL EQUITY</td>
                    <td className="amount">₱{totalEquity.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '10px' }}></td></tr>

                  <tr style={{ backgroundColor: '#1a237e', color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                    <td>TOTAL LIABILITIES AND EQUITY</td>
                    <td className="amount">₱{totalLiabilitiesAndEquity.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'income' && (
            <div>
              <h3 style={{ textAlign: 'center', marginBottom: '10px', color: '#1a237e' }}>
                DEWFOAM ACCOUNTING SYSTEM
              </h3>
              <h4 style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
                Income Statement (Statement of Comprehensive Income)
              </h4>
              <p style={{ textAlign: 'center', marginBottom: '30px', color: '#666' }}>
                For the period {new Date(periodStart).toLocaleDateString('en-US')} to {new Date(periodEnd).toLocaleDateString('en-US')}
              </p>

              <table className="data-table">
                <tbody>
                  <tr style={{ backgroundColor: '#1a237e', color: 'white' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', fontSize: '16px' }}>REVENUE</td>
                  </tr>
                  {incomeStatementData.revenue.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '20px' }}>Net Sales</td>
                    <td className="amount">₱{netSales.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '10px' }}></td></tr>

                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <td style={{ fontWeight: 'bold', paddingLeft: '20px' }}>Cost of Goods Sold</td>
                    <td className="amount">₱{totalCOGS.toLocaleString()}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '15px' }}>
                    <td style={{ paddingLeft: '20px' }}>GROSS PROFIT</td>
                    <td className="amount">₱{grossProfit.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '10px' }}></td></tr>

                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', paddingLeft: '20px' }}>Operating Expenses</td>
                  </tr>
                  {incomeStatementData.operatingExpenses.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '30px' }}>Total Operating Expenses</td>
                    <td className="amount">₱{totalOperatingExpenses.toLocaleString()}</td>
                  </tr>
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '15px' }}>
                    <td style={{ paddingLeft: '20px' }}>OPERATING INCOME</td>
                    <td className="amount">₱{operatingIncome.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '10px' }}></td></tr>

                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', paddingLeft: '20px' }}>Other Income</td>
                  </tr>
                  {incomeStatementData.otherIncome.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '30px' }}>Total Other Income</td>
                    <td className="amount">₱{totalOtherIncome.toLocaleString()}</td>
                  </tr>

                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', paddingLeft: '20px' }}>Other Expenses</td>
                  </tr>
                  {incomeStatementData.otherExpenses.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount">₱{item.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td style={{ paddingLeft: '30px' }}>Total Other Expenses</td>
                    <td className="amount">₱{totalOtherExpenses.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '10px' }}></td></tr>

                  <tr style={{ backgroundColor: '#1a237e', color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                    <td>NET INCOME</td>
                    <td className="amount">₱{netIncome.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'cashflow' && (
            <div>
              <h3 style={{ textAlign: 'center', marginBottom: '10px', color: '#1a237e' }}>
                DEWFOAM ACCOUNTING SYSTEM
              </h3>
              <h4 style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
                Cash Flow Statement
              </h4>
              <p style={{ textAlign: 'center', marginBottom: '30px', color: '#666' }}>
                For the period {new Date(periodStart).toLocaleDateString('en-US')} to {new Date(periodEnd).toLocaleDateString('en-US')}
              </p>

              <table className="data-table">
                <tbody>
                  <tr style={{ backgroundColor: '#1a237e', color: 'white' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', fontSize: '16px' }}>CASH FLOWS FROM OPERATING ACTIVITIES</td>
                  </tr>
                  {cashFlowData.operating.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount" style={{ color: item.amount < 0 ? '#dc3545' : 'inherit' }}>
                        {item.amount >= 0 ? '₱' : '(₱'}{Math.abs(item.amount).toLocaleString()}{item.amount < 0 ? ')' : ''}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '15px' }}>
                    <td style={{ paddingLeft: '20px' }}>Net Cash from Operating Activities</td>
                    <td className="amount">₱{netCashFromOperating.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '15px' }}></td></tr>

                  <tr style={{ backgroundColor: '#1a237e', color: 'white' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', fontSize: '16px' }}>CASH FLOWS FROM INVESTING ACTIVITIES</td>
                  </tr>
                  {cashFlowData.investing.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount" style={{ color: item.amount < 0 ? '#dc3545' : 'inherit' }}>
                        {item.amount >= 0 ? '₱' : '(₱'}{Math.abs(item.amount).toLocaleString()}{item.amount < 0 ? ')' : ''}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '15px' }}>
                    <td style={{ paddingLeft: '20px' }}>Net Cash from Investing Activities</td>
                    <td className="amount" style={{ color: netCashFromInvesting < 0 ? '#dc3545' : 'inherit' }}>
                      {netCashFromInvesting >= 0 ? '₱' : '(₱'}{Math.abs(netCashFromInvesting).toLocaleString()}{netCashFromInvesting < 0 ? ')' : ''}
                    </td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '15px' }}></td></tr>

                  <tr style={{ backgroundColor: '#1a237e', color: 'white' }}>
                    <td colSpan={2} style={{ fontWeight: 'bold', fontSize: '16px' }}>CASH FLOWS FROM FINANCING ACTIVITIES</td>
                  </tr>
                  {cashFlowData.financing.map((item, index) => (
                    <tr key={index}>
                      <td style={{ paddingLeft: '40px' }}>{item.account}</td>
                      <td className="amount" style={{ color: item.amount < 0 ? '#dc3545' : 'inherit' }}>
                        {item.amount >= 0 ? '₱' : '(₱'}{Math.abs(item.amount).toLocaleString()}{item.amount < 0 ? ')' : ''}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '15px' }}>
                    <td style={{ paddingLeft: '20px' }}>Net Cash from Financing Activities</td>
                    <td className="amount">₱{netCashFromFinancing.toLocaleString()}</td>
                  </tr>

                  <tr><td colSpan={2} style={{ height: '15px' }}></td></tr>

                  <tr style={{ backgroundColor: '#1a237e', color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                    <td>NET INCREASE IN CASH</td>
                    <td className="amount">₱{netCashIncrease.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default FinancialStatements;
